// src/pages/admin/users/ClientDetails.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Form,
  Button,
  Collapse,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import ConfirmModal from "../../../components/Common/ConfirmModal";
import withRouter from "../../../components/Common/withRouter";
import {
  useCreateUser,
  useUserDetails,
  useUpdateClientByAdmin,
} from "../../../api/users";
import { mediaUrl } from "../../../helpers/mediaHelper";
import { useFormik } from "formik";
import * as Yup from "yup";
import toastr from "toastr";
import {
  AdminActionCard,
  AdminClientApprovalFormSections,
  AdminFormFooter,
  buildClientInsurancePayload,
  clientAccountAccessValidationShape,
  clientInsuranceValidationShape,
  ClientInsuranceReadOnlyPanel,
  initialUpFrontPaymentForTier,
  INSURANCE_REASON_OPTIONS,
  normalizeClientStatus,
  resolveUpFrontPaymentPercentageForTier,
} from "../clientAdminApprovalUi";

/** ---------------- Helpers ---------------- */
const toInputDate = (val) => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val; // already YYYY-MM-DD
  const m1 = val.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/); // DD-MM-YYYY or DD/MM/YYYY
  if (m1) {
    const [, dd, mm, yyyy] = m1;
    return `${yyyy}-${mm}-${dd}`;
  }
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const safeText = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map((x) => safeText(x)).join(", ");
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {
      // fall through and treat as a plain single URL/path string
    }
    return [trimmed];
  }
  return [];
};

const isCorporateOwnershipType = (type) => type === "corporate";
const isIndividualOwnershipType = (type) =>
  type === "individuals" || type === "individual";

const formatOwnershipPercent = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  return Number.isFinite(n) ? `${n}%` : "-";
};

const STATUS_OPTIONS = ["approved", "declined", "actionRequired"];
const TIER_OPTIONS = ["top", "entry", "lower"];
const OCD_STATUS_OPTIONS = ["cleared", "flagged"];

/** Map API values to form options (e.g. legacy `none` tier). */
const tierValueForForm = (tier) =>
  TIER_OPTIONS.includes(tier) ? tier : "entry";

const formatDisplayDate = (val) => {
  const input = toInputDate(val);
  if (!input) return "-";
  const [yyyy, mm, dd] = input.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

const creditFromClientUser = (user, client) => {
  const raw =
    client?.wallet?.credit ?? user?.credit ?? client?.credit ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

/** ---------------- Component ---------------- */
function ClientDetails(props) {
  const { slug } = props.router?.params || {};
  const isProfileUpdateRequest = new URLSearchParams(
    props.router?.location?.search || ""
  ).has("updated");

  // Hooks must be called unconditionally and in the same order every render
  const userDetailsMutation = useUserDetails();
  const createUser = useCreateUser();
  const updateClient = useUpdateClientByAdmin();
  const [pendingCreatePayload, setPendingCreatePayload] = useState(null);

  useEffect(() => {
    if (!slug) return;
    try {
      userDetailsMutation.mutate(slug);
    } catch {
      /* react-query handles errors */
    }
  }, [slug]);

  // axios-safe extraction
  const raw =
    (userDetailsMutation?.data &&
      (userDetailsMutation?.data?.data ?? userDetailsMutation?.data)) ||
    {};
  const user = (raw && typeof raw === "object" ? raw : {}) || {};
  const client = (user && user.client) || {};

  // memoize slices to avoid flicker between renders
  const {
    companyInformation: ci = {},
    primaryInformation: pi = {},
    financeBilling: fb = {},
    ownershipControlDeclaration: ocd = {},
    declarationAndConsent: dac = {},
    insurance: ins = {},
    healthAndSafetyContact: hs = {},
  } = useMemo(
    () => ({
      companyInformation: client.companyInformation || {},
      primaryInformation: client.primaryInformation || {},
      financeBilling: client.financeBilling || {},
      ownershipControlDeclaration: client.ownershipControlDeclaration || {},
      declarationAndConsent: client.declarationAndConsent || {},
      insurance: client.insurance || {},
      healthAndSafetyContact: client.healthAndSafetyContact || {},
    }),
    [client]
  );

  // Admin already created the platform user → update flow; otherwise create
  const hasAdminCreated = !!user?.hasAdminCreated;
  // Profile Update Requests: existing users — always update, never create
  const canUpdateClient = hasAdminCreated || isProfileUpdateRequest;

  // Get status options (no filtering needed since pending is removed)
  const getStatusOptions = () => {
    return STATUS_OPTIONS;
  };

  // prefill from GET payload
  const emailFromData = user?.email || "";
  const statusFromData = normalizeClientStatus(user?.status || "approved");
  const tierFromData = tierValueForForm(user?.tier || "entry");

  // Normalize OCD values from GET
  const ocdStatusFromData = OCD_STATUS_OPTIONS.includes(ocd?.status)
    ? ocd.status
    : "cleared";
  const ocdReasonFromData = typeof ocd?.reason === "string" ? ocd.reason : "";
  const actionRequiredReasonFromData =
    typeof user?.reason === "string" ? user.reason : "";
  const creditFromData = creditFromClientUser(user, client);

  /** -------- Formik: Create (client-only) -------- */
  const createInitialValues = useMemo(
    () => ({
      email: emailFromData,
      status: STATUS_OPTIONS.includes(statusFromData)
        ? statusFromData
        : "approved",
      tier: tierFromData,
      reason: actionRequiredReasonFromData,
      upFrontPaymentPercentage: initialUpFrontPaymentForTier(
        tierFromData,
        client?.upFrontPaymentPercentage
      ),
      credit: creditFromData,
      netPaymentTerms: Number(
        user?.netPaymentTerms ?? client?.netPaymentTerms ?? 0
      ),

      ocdStatus: ocdStatusFromData,
      ocdReason: ocdReasonFromData,
      isPublicLiabilityVerified: !!ins?.isPublicLiabilityVerified,
      isEmployersLiabilityVerified: !!ins?.isEmployersLiabilityVerified,
      publicLiabilityExpiryDate: toInputDate(ins?.publicLiabilityExpiryDate),
      employersLiabilityExpiryDate: toInputDate(
        ins?.employersLiabilityExpiryDate
      ),
      publicLiabilityReason: ins?.publicLiabilityReason || "",
      employersLiabilityReason: ins?.employersLiabilityReason || "",
    }),
    [
      emailFromData,
      statusFromData,
      tierFromData,
      actionRequiredReasonFromData,
      client?.upFrontPaymentPercentage,
      creditFromData,
      user?.netPaymentTerms,
      client?.netPaymentTerms,
      ocdStatusFromData,
      ocdReasonFromData,
      ins?.isPublicLiabilityVerified,
      ins?.isEmployersLiabilityVerified,
      ins?.publicLiabilityExpiryDate,
      ins?.employersLiabilityExpiryDate,
      ins?.publicLiabilityReason,
      ins?.employersLiabilityReason,
    ]
  );

  const createFormik = useFormik({
    enableReinitialize: true,
    initialValues: createInitialValues,
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
      status: Yup.mixed()
        .oneOf(STATUS_OPTIONS)
        .required("Status is required"),
      tier: Yup.mixed().oneOf(TIER_OPTIONS).required("Tier is required"),
      ...clientAccountAccessValidationShape(Yup),

      ocdStatus: Yup.mixed()
        .oneOf(OCD_STATUS_OPTIONS, "Invalid status")
        .required("Required"),
      ocdReason: Yup.string().when("ocdStatus", {
        is: "flagged",
        then: (schema) =>
          schema.trim().required("Reason is required when flagged"),
        otherwise: (schema) => schema.notRequired(),
      }),
      ...clientInsuranceValidationShape(Yup),
    }),
    onSubmit: (values) => {
      try {
        const {
          email,
          status,
          tier,
          reason,
          credit,
          netPaymentTerms,
          ocdStatus,
          ocdReason,
        } = values;

        const trimmedReason =
          typeof reason === "string" && reason.trim()
            ? reason.trim()
            : undefined;
        const includeReason =
          normalizeClientStatus(status) === "actionRequired" && !!trimmedReason;

        const body = {
          email,
          role: "client",
          status,
          tier,
          ...(includeReason ? { reason: trimmedReason } : {}),
          upFrontPaymentPercentage: resolveUpFrontPaymentPercentageForTier(
            tier,
            values.upFrontPaymentPercentage
          ),
          ...(tier === "top" ? { credit: Number(credit) } : {}),
          netPaymentTerms: Number(netPaymentTerms),
          client: {
            insurance: buildClientInsurancePayload(values),
            ownershipControlDeclaration: {
              status: ocdStatus,
              ...(ocdStatus === "flagged" && ocdReason
                ? { reason: ocdReason.trim() }
                : {}),
            },
          },
        };

        setPendingCreatePayload(body);
      } catch (err) {
        console.error("Create submit error:", err);
      }
    },
  });

  const isDecliningClient =
    normalizeClientStatus(createFormik.values.status) === "declined";

  const closeCreateConfirmation = () => {
    if (!createUser?.isPending) {
      setPendingCreatePayload(null);
    }
  };

  const confirmCreateClient = () => {
    if (!pendingCreatePayload || createUser?.isPending) return;

    const isDeclineCreate =
      normalizeClientStatus(pendingCreatePayload?.status) === "declined";

    createUser.mutate(
      { payload: pendingCreatePayload },
      {
        onSuccess: () => {
          setPendingCreatePayload(null);
          if (isDeclineCreate) {
            toastr.success("User declined successfully!", "", {
              toastClass: "toast-error",
              iconClass: "toast-error",
            });
          } else {
            toastr.success("User created successfully!");
          }
          props.router.navigate("/user/list");
        },
      }
    );
  };

  /** -------- Formik: Update (admin-created / profile update) -------- */
  const updateInitialValues = useMemo(
    () => ({
      email: emailFromData,
      status: STATUS_OPTIONS.includes(statusFromData)
        ? statusFromData
        : "approved",
      tier: tierFromData,
      reason: actionRequiredReasonFromData,
      upFrontPaymentPercentage: initialUpFrontPaymentForTier(
        tierFromData,
        client?.upFrontPaymentPercentage
      ),
      credit: creditFromData,
      netPaymentTerms: Number(
        user?.netPaymentTerms ?? client?.netPaymentTerms ?? 0
      ),
      ocdStatus: ocdStatusFromData,
      ocdReason: ocdReasonFromData,
      isPublicLiabilityVerified: !!ins?.isPublicLiabilityVerified,
      isEmployersLiabilityVerified: !!ins?.isEmployersLiabilityVerified,
      publicLiabilityExpiryDate: toInputDate(ins?.publicLiabilityExpiryDate),
      employersLiabilityExpiryDate: toInputDate(
        ins?.employersLiabilityExpiryDate
      ),
      publicLiabilityReason: ins?.publicLiabilityReason || "",
      employersLiabilityReason: ins?.employersLiabilityReason || "",
    }),
    [
      emailFromData,
      statusFromData,
      tierFromData,
      actionRequiredReasonFromData,
      client?.upFrontPaymentPercentage,
      creditFromData,
      user?.netPaymentTerms,
      client?.netPaymentTerms,
      ocdStatusFromData,
      ocdReasonFromData,
      ins?.isPublicLiabilityVerified,
      ins?.isEmployersLiabilityVerified,
      ins?.publicLiabilityExpiryDate,
      ins?.employersLiabilityExpiryDate,
      ins?.publicLiabilityReason,
      ins?.employersLiabilityReason,
    ]
  );

  const updateFormik = useFormik({
    enableReinitialize: true,
    initialValues: updateInitialValues,
    validationSchema: Yup.object({
      status: Yup.mixed()
        .oneOf(getStatusOptions())
        .required("Status is required"),
      tier: Yup.mixed().oneOf(TIER_OPTIONS).required("Tier is required"),
      ...clientAccountAccessValidationShape(Yup),
      ocdStatus: Yup.mixed()
        .oneOf(OCD_STATUS_OPTIONS, "Invalid status")
        .required("Required"),
      ocdReason: Yup.string()
        .oneOf(INSURANCE_REASON_OPTIONS, "Invalid reason")
        .when("ocdStatus", {
          is: "flagged",
          then: (schema) => schema.required("Reason is required when flagged"),
          otherwise: (schema) => schema.notRequired(),
        }),
      ...clientInsuranceValidationShape(Yup),
    }),
    onSubmit: (values, formikHelpers) => {
      try {
        // Mark all fields as touched to show validation errors
        const fieldsToTouch = [
          "status",
          "tier",
          "reason",
          "upFrontPaymentPercentage",
          "credit",
          "netPaymentTerms",
          "ocdStatus",
          "ocdReason",
          "isPublicLiabilityVerified",
          "isEmployersLiabilityVerified",
          "publicLiabilityExpiryDate",
          "employersLiabilityExpiryDate",
          "publicLiabilityReason",
          "employersLiabilityReason",
        ];

        fieldsToTouch.forEach((field) => {
          formikHelpers.setTouched({ ...formikHelpers.touched, [field]: true });
        });

        // Trigger validation
        formikHelpers.validateForm().then((errors) => {
          // Only proceed if form is valid
          if (Object.keys(errors).length === 0) {
            const {
              status,
              tier,
              reason,
              credit,
              netPaymentTerms,
              ocdStatus,
              ocdReason,
            } = values;

            const trimmedReason =
              typeof reason === "string" && reason.trim()
                ? reason.trim()
                : undefined;
            const includeReason =
              normalizeClientStatus(status) === "actionRequired" &&
              !!trimmedReason;

            const body = {
              slug,
              status,
              tier,
              ...(includeReason ? { reason: trimmedReason } : {}),
              upFrontPaymentPercentage: resolveUpFrontPaymentPercentageForTier(
                tier,
                values.upFrontPaymentPercentage
              ),
              ...(tier === "top" ? { credit: Number(credit) } : {}),
              netPaymentTerms: Number(netPaymentTerms),
              client: {
                insurance: buildClientInsurancePayload(values),
                ownershipControlDeclaration: (() => {
                  const obj = { status: ocdStatus };
                  if (ocdStatus === "flagged" && ocdReason)
                    obj.reason = ocdReason;
                  return obj;
                })(),
              },
            };

            updateClient.mutate(
              {
                payload: body,
                ...(isProfileUpdateRequest
                  ? { fieldStatus: "updated" }
                  : {}),
              },
              {
                onSuccess: () => {
                  toastr.success(
                    isProfileUpdateRequest
                      ? "User updated successfully!"
                      : "Client updated successfully!"
                  );
                  if (isProfileUpdateRequest) {
                    props.router.navigate("/user/list?updated=true");
                  } else {
                    userDetailsMutation.mutate(slug);
                  }
                },
              }
            );
          } else {
            // Show error message if validation fails
            toastr.error("Please fill in all required fields correctly");
          }
        });
      } catch (err) {
        console.error("Update submit error:", err);
      }
    },
  });

  /** -------- Derived + UI state (after all Formik hooks) -------- */
  // Collapsible sections
  const [openCreate, setOpenCreate] = useState(!canUpdateClient);
  const [openUpdate, setOpenUpdate] = useState(true);
  const [openCompany, setOpenCompany] = useState(true);
  const [openPrimary, setOpenPrimary] = useState(true);
  const [openAccount, setOpenAccount] = useState(true);
  const [openFinance, setOpenFinance] = useState(true);
  const [openOCD, setOpenOCD] = useState(true);
  const [openInsurance, setOpenInsurance] = useState(true);
  const [openHealth, setOpenHealth] = useState(true);

  // Checkbox states for update form
  const [updateCheckboxes, setUpdateCheckboxes] = useState({
    isPublicLiabilityVerified: false,
    isEmployersLiabilityVerified: false,
  });

  // Sync checkbox states when data loads
  useEffect(() => {
    if (ins && Object.keys(ins).length > 0) {
      setUpdateCheckboxes({
        isPublicLiabilityVerified: !!ins?.isPublicLiabilityVerified,
        isEmployersLiabilityVerified: !!ins?.isEmployersLiabilityVerified,
      });
    }
  }, [ins]);

  /** -------- Small UI helpers -------- */
  const handleInsuranceVerifiedChange = (formik, field, checked) => {
    formik.setFieldValue(field, checked);
    if (field === "isEmployersLiabilityVerified") {
      if (checked) {
        formik.setFieldValue("employersLiabilityReason", "");
      } else {
        formik.setFieldValue("employersLiabilityExpiryDate", "");
      }
    }
    if (field === "isPublicLiabilityVerified") {
      if (checked) {
        formik.setFieldValue("publicLiabilityReason", "");
      } else {
        formik.setFieldValue("publicLiabilityExpiryDate", "");
      }
    }
  };

  const handleUpdateCheckboxChange = (field, checked) => {
    setUpdateCheckboxes((prev) => ({ ...prev, [field]: checked }));
    handleInsuranceVerifiedChange(updateFormik, field, checked);
  };

  const handleCreateInsuranceVerifiedChange = (field, checked) => {
    handleInsuranceVerifiedChange(createFormik, field, checked);
  };

  const BoolIcon = ({ value }) =>
    value === true ? (
      <i className="mdi mdi-check-circle-outline me-1 text-success fs-4" />
    ) : value === false ? (
      <i className="mdi mdi-close-circle-outline me-1 text-danger fs-4" />
    ) : (
      "-"
    );

  const ListLinks = (arr) =>
    Array.isArray(arr) && arr.length ? (
      <ul className="mb-0 ps-3">
        {arr.map((url, idx) => (
          <li key={idx}>
            <a
              href={mediaUrl(url)}
              target="_blank"
              rel="noreferrer"
              className="text-decoration-underline text-secondary"
            >
              Click here to view
            </a>
          </li>
        ))}
      </ul>
    ) : (
      "-"
    );

  // centralize loading / error flags (NO early returns that skip hooks)
  const loading = !!userDetailsMutation?.isPending;
  const err = userDetailsMutation?.error;
  const errMsg =
    err &&
    safeText(
      err?.friendlyMessage ??
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        err
    );
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Users" breadcrumbItem="Client Details" />

        {loading && (
          <div
            className="text-center"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Spinner
              color="primary"
              className="mb-4"
              style={{ width: "4rem", height: "4rem" }}
            />
          </div>
        )}

        {err && (
          <div className="text-danger">
            <pre className="mb-0">{errMsg}</pre>
          </div>
        )}

        {!loading && !err && (
          <>
            {/* Name banner */}
            <Card className="border-0">
              <CardBody className="bg-primary text-white py-4 px-3 rounded">
                <h5 className="mb-0 text-white">
                  {user?.fullName || user?.email || "Client"}
                </h5>
              </CardBody>
            </Card>

            {/* ===== Company Information ===== */}
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenCompany((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">Company Information</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openCompany ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openCompany}>
                <CardBody>
                  <Row className="gy-2">
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Registered Name
                      </small>
                      <div>{ci?.registeredCompanyName || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Trading Name
                      </small>
                      <div>{ci?.tradingName || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Company Reg. #
                      </small>
                      <div>{ci?.companyRegistrationNumber || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">VAT Reg. #</small>
                      <div>{ci?.vatRegistrationNumber || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Registered Office Address
                      </small>
                      <div>{ci?.registeredOfficeAddress || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Trading Address
                      </small>
                      <div>{ci?.tradingAddress || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Organisation Type
                      </small>
                      <div>{ci?.organisationType || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Industry Sector
                      </small>
                      <div>{ci?.industrySector || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">Website</small>
                      <div>
                        {ci?.companyWebsite ? (
                          <a
                            href={ci.companyWebsite}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {ci.companyWebsite}
                          </a>
                        ) : (
                          "-"
                        )}
                      </div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Disability Friendly Workplace
                      </small>
                      <div>{ci?.isWorkplaceDisabilityFriendly || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Second Chance Hiring
                      </small>
                      <div>{ci?.secondChanceHiring || "-"}</div>
                    </Col>
                  </Row>
                </CardBody>
              </Collapse>
            </Card>

            {/* ===== Primary Information ===== */}
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenPrimary((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">
                  Primary Information
                  {user?.fullName ? ` — ${user.fullName}` : ""}
                </h5>
                <i
                  className={`mdi mdi-chevron-${
                    openPrimary ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openPrimary}>
                <CardBody>
                  <Row className="gy-2">
                    <Col md={3}>
                      <small className="text-muted fw-bolder">Full Name</small>
                      <div>{user?.fullName || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">Position</small>
                      <div>{pi?.position || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">Email</small>
                      <div>{user?.email || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">Phone</small>
                      <div>{user?.phoneNumber || "-"}</div>
                    </Col>
                    {user?.hasContractSigned && (
                      <Col md={3}>
                        <small className="text-muted fw-bolder">
                          Contract Signed Date
                        </small>
                        <div>
                          {user?.contractSignedAt
                            ? new Date(
                                user.contractSignedAt
                              ).toLocaleDateString()
                            : "-"}
                        </div>
                      </Col>
                    )}
                    {/* <Col md={3}>
                      <small className="text-muted fw-bolder">Role</small>
                      <div className="text-capitalize">{user?.role || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">Status</small>
                      <div>
                        <StatusBadge status={user?.status} />
                      </div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">Tier</small>
                      <div className="text-capitalize">{user?.tier || "-"}</div>
                    </Col> */}
                  </Row>
                </CardBody>
              </Collapse>
            </Card>

            {/* ===== Health & Safety Contact ===== */}
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenHealth((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">Health &amp; Safety Contact</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openHealth ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openHealth}>
                <CardBody>
                  <Row className="gy-2">
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Full Name</small>
                      <div>{hs?.fullName || "-"}</div>
                    </Col>
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Email</small>
                      <div>{hs?.email || "-"}</div>
                    </Col>
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Phone Number
                      </small>
                      <div>{hs?.phoneNumber || "-"}</div>
                    </Col>
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Position</small>
                      <div>{hs?.position || "-"}</div>
                    </Col>
                  </Row>
                </CardBody>
              </Collapse>
            </Card>

            {/* ===== Declaration & Consent ===== */}
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenAccount((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">Declaration &amp; Consent</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openAccount ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openAccount}>
                <CardBody>
                  <Row className="gy-2">
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Confirm Information Is Accurate
                      </small>
                      <div>
                        <BoolIcon
                          value={
                            dac?.confirmInformationProvidedIsTrueAndAccurate
                          }
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Agree Terms &amp; Privacy
                      </small>
                      <div>
                        <BoolIcon value={dac?.agreeTermsAndPrivacyNotice} />
                      </div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Marketing Consent
                      </small>
                      <div>
                        <BoolIcon
                          value={dac?.consentToReceivingMarketingInformation}
                        />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Collapse>
            </Card>

            {/* ===== Finance & Billing ===== */}
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenFinance((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">Finance Details</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openFinance ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openFinance}>
                <CardBody>
                  <Row className="gy-2">
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Finance Contact Name
                      </small>
                      <div>{fb?.fullName || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Finance Email
                      </small>
                      <div>{fb?.email || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Finance Phone
                      </small>
                      <div>{fb?.phone || "-"}</div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted fw-bolder">
                        Finance Position
                      </small>
                      <div>{fb?.position || "-"}</div>
                    </Col>
                  </Row>
                </CardBody>
              </Collapse>
            </Card>

            {/* ===== Ownership & Control Declaration ===== */}
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenOCD((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">Ownership &amp; Control Declaration</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openOCD ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openOCD}>
                <CardBody className="py-3">
                  <Row className="g-3">
                    <Col md={12}>
                      <div className="worker-admin-rtw-group mb-0">
                        <p className="worker-admin-rtw-group__label mb-2">
                          Company &amp; declaration
                        </p>
                        <Row className="g-2">
                          <Col md={5} sm={12}>
                            <small className="text-muted d-block">
                              Directors / beneficial owners
                            </small>
                            <div className="small mb-0">
                              {ocd?.directorsOrBeneficialOwners || "-"}
                            </div>
                          </Col>
                          <Col md={4} sm={6}>
                            <small className="text-muted d-block">
                              Ownership company type
                            </small>
                            <div className="small text-capitalize mb-0">
                              {ocd?.ownershipCompanyType || "-"}
                            </div>
                          </Col>
                          <Col md={3} sm={6}>
                            <small className="text-muted d-block">
                              Confirmed
                            </small>
                            <div className="mb-0">
                              <BoolIcon value={ocd?.confirmed} />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="worker-admin-rtw-group mb-0">
                        <p className="worker-admin-rtw-group__label mb-2">
                          Director documents
                        </p>
                        <Row className="g-2">
                          <Col md={6}>
                            <small className="text-muted d-block">
                              ID files
                            </small>
                            {ListLinks(normalizeToArray(ocd?.directorIdFiles))}
                          </Col>
                          <Col md={6}>
                            <small className="text-muted d-block">
                              Proof of address
                            </small>
                            {ListLinks(
                              normalizeToArray(ocd?.directorProofOfAddressFiles)
                            )}
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    {isCorporateOwnershipType(ocd?.ownershipCompanyType) && (
                      <Col md={12}>
                        <div className="worker-admin-rtw-group mb-0">
                          <p className="worker-admin-rtw-group__label mb-2">
                            Company ownership structure
                          </p>
                          <Row className="g-2">
                            <Col md={12}>
                              <small className="text-muted d-block">
                                Ownership structure PDF
                              </small>
                              {ListLinks(
                                normalizeToArray(
                                  ocd?.companyOwnershipStructurePDF
                                )
                              )}
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    )}

                    <Col md={12}>
                      <div className="worker-admin-rtw-group mb-0">
                        <p className="worker-admin-rtw-group__label mb-2">
                          Owners
                        </p>
                        {Array.isArray(ocd?.ownershipEntries) &&
                        ocd.ownershipEntries.length ? (
                          <div className="table-responsive">
                            <Table
                              size="sm"
                              bordered
                              responsive
                              className="mb-0 align-middle"
                            >
                              <thead className="table-light">
                                <tr>
                                  <th>Name</th>
                                  <th style={{ width: "12rem" }}>%</th>
                                  {isCorporateOwnershipType(
                                    ocd?.ownershipCompanyType
                                  ) && (
                                    <>
                                      <th>ID files</th>
                                      <th>Proof of address</th>
                                    </>
                                  )}
                                  {isIndividualOwnershipType(
                                    ocd?.ownershipCompanyType
                                  ) && (
                                    <>
                                      <th>ID files</th>
                                      <th>Proof of address</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {ocd.ownershipEntries.map((o, i) => (
                                  <tr
                                    key={o?.id || `${o?.name || "owner"}-${i}`}
                                  >
                                    <td className="small">{o?.name || "-"}</td>
                                    <td className="small">
                                      {formatOwnershipPercent(
                                        o?.percentageOfOwnership
                                      )}
                                    </td>
                                    {isCorporateOwnershipType(
                                      ocd?.ownershipCompanyType
                                    ) && (
                                      <>
                                        <td className="small">
                                          {ListLinks(
                                            normalizeToArray(
                                              o?.corporateOwnerIdFiles
                                            )
                                          )}
                                        </td>
                                        <td className="small">
                                          {ListLinks(
                                            normalizeToArray(
                                              o?.corporateOwnerProofOfAddressFiles
                                            )
                                          )}
                                        </td>
                                      </>
                                    )}
                                    {isIndividualOwnershipType(
                                      ocd?.ownershipCompanyType
                                    ) && (
                                      <>
                                        <td className="small">
                                          {ListLinks(
                                            normalizeToArray(
                                              o?.individualOwnerIdFiles
                                            )
                                          )}
                                        </td>
                                        <td className="small">
                                          {ListLinks(
                                            normalizeToArray(
                                              o?.individualOwnerProofOfAddressFiles
                                            )
                                          )}
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <div className="small text-muted">-</div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Collapse>
            </Card>

            {/* ===== Insurance ===== */}
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenInsurance((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">Insurance</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openInsurance ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openInsurance}>
                <CardBody>
                  <ClientInsuranceReadOnlyPanel
                    ins={ins}
                    normalizeToArray={normalizeToArray}
                    ListLinks={ListLinks}
                    BoolIcon={BoolIcon}
                    formatDisplayDate={formatDisplayDate}
                  />
                </CardBody>
              </Collapse>
            </Card>

            {/* ===== Create (first-time approval only) ===== */}
            {!canUpdateClient && (
              <AdminActionCard
                variant="create"
                title={isDecliningClient ? "Decline user" : "Create User"}
                subtitle="Verify insurance, ownership checks, and account settings before activating this client."
                isOpen={openCreate}
                onToggle={() => setOpenCreate((v) => !v)}
              >
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    createFormik.handleSubmit();
                  }}
                >
                  <AdminClientApprovalFormSections
                    variant="create"
                    formik={createFormik}
                    safeText={safeText}
                    statusOptions={STATUS_OPTIONS}
                    tierOptions={TIER_OPTIONS}
                    ocdStatusOptions={OCD_STATUS_OPTIONS}
                    isVerified={(field) => !!createFormik.values[field]}
                    onInsuranceVerifiedChange={
                      handleCreateInsuranceVerifiedChange
                    }
                  />
                  <AdminFormFooter
                    hint={
                      isDecliningClient
                        ? "Confirm decline after reviewing the client application."
                        : "Review insurance verification and ownership status before creating the user."
                    }
                  >
                    <Button
                      color={isDecliningClient ? "danger" : "success"}
                      type="submit"
                      disabled={!!createUser?.isPending}
                      className="px-4"
                    >
                      <i
                        className={`mdi ${
                          isDecliningClient
                            ? "mdi-close-circle-outline"
                            : "mdi-check-circle-outline"
                        } me-1`}
                      />
                      {createUser?.isPending
                        ? isDecliningClient
                          ? "Declining..."
                          : "Creating..."
                        : isDecliningClient
                          ? "Decline user"
                          : "Create User"}
                    </Button>
                  </AdminFormFooter>
                </Form>
              </AdminActionCard>
            )}

            {/* ===== Update (hasAdminCreated or Profile Update Request) ===== */}
            {canUpdateClient && (
              <AdminActionCard
                variant="update"
                title="Update user"
                subtitle="Review insurance verification, ownership status, and account settings."
                isOpen={openUpdate}
                onToggle={() => setOpenUpdate((v) => !v)}
              >
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    updateFormik.handleSubmit();
                  }}
                >
                  <AdminClientApprovalFormSections
                    variant="update"
                    formik={updateFormik}
                    safeText={safeText}
                    statusOptions={getStatusOptions()}
                    tierOptions={TIER_OPTIONS}
                    ocdStatusOptions={OCD_STATUS_OPTIONS}
                    isVerified={(field) => !!updateCheckboxes[field]}
                    onInsuranceVerifiedChange={handleUpdateCheckboxChange}
                  />
                  <AdminFormFooter hint="Save changes after reviewing insurance and compliance fields.">
                    <Button
                      color="primary"
                      type="submit"
                      disabled={!!updateClient?.isPending}
                      className="px-4"
                    >
                      <i className="mdi mdi-content-save-outline me-1" />
                      {updateClient?.isPending ? "Updating..." : "Update user"}
                    </Button>
                  </AdminFormFooter>
                </Form>
              </AdminActionCard>
            )}
          </>
        )}
        <ConfirmModal
          isOpen={!!pendingCreatePayload}
          toggle={closeCreateConfirmation}
          title={
            isDecliningClient
              ? "Confirm decline"
              : "Confirm Company Checks"
          }
          confirmLabel={
            isDecliningClient
              ? "Confirm and Decline User"
              : "Confirm and Create Client"
          }
          cancelLabel="Go Back"
          confirmColor={isDecliningClient ? "danger" : "success"}
          onConfirm={confirmCreateClient}
          isLoading={!!createUser?.isPending}
        >
          {isDecliningClient ? (
            <>
              <p className="mb-3">
                You are about to decline this client&apos;s application. They
                will not be approved for the platform.
              </p>
              <p className="text-muted mb-0">
                Confirm only if you are sure you want to decline this user.
              </p>
            </>
          ) : (
            <>
              <p className="mb-3">
                Have all required company and compliance checks been completed
                and approved?
              </p>
              <p className="text-muted mb-0">
                Only proceed if the client is authorised to use the platform.
              </p>
            </>
          )}
        </ConfirmModal>
      </div>
    </div>
  );
}

export default withRouter(ClientDetails);
